import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import appConfig from '@/config/appConfig.typed';
import {appendQueryStringTemplate} from '@/hooks/externalLink/externalLinkQueryUtils';
import appErrorService from '@/services/app/appErrorService';
import externalLinkService from '@/services/app/externalLinkService';
import iccSsoService from '@/services/icc/iccSsoService';
import {isSsoCookieFlowEnabledForLink} from '@/services/sso/ssoIccSessionInfoPolicy';
import {
    resolveSsoLinkUrlForOpenFromState,
    shouldRequestSessionInfo,
} from '@/services/sso/ssoIccSessionInfoPolicy.wired';
import userBrowserService from '@/services/user/userBrowserService';
import {makeSelectSsoLink} from '@/state/selectors/media';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch, getState} from '@/state/store';
import helpers from '@/utils/helpers';

import useSsoLinkRefresh from './useSsoLinkRefresh';

const URL_LOADER_NAME = 'URL_LOADER_NAME';
let urls: string[] = [];
let ssoTemplates: string[] = [];
let batchAllowSsoCookies = false;

export type UseSsoLinkBatchParams = {
    ssoTemplate?: string;
    url?: string;
    params?: Record<string, unknown>;
    disabled?: boolean;
    queryStringTemplate?: string;
    openInBluefy?: boolean;
    isSsoCookiesForced?: boolean;
    linkProcessor?: (url: string) => string;
    deviceType?: string;
    deviceSerialNumber?: string;
};

export type UseSsoLinkBatchResult = {
    redirect: () => void | Promise<void>;
    link: string | undefined;
};

/**
 * DCX-oriented batch path: debounced CreateLoginTokenList (getSsoLinks) + shared Redux cache.
 */
const useSsoLinkBatch = ({
    ssoTemplate,
    url,
    params,
    disabled,
    queryStringTemplate,
    openInBluefy,
    isSsoCookiesForced = false,
    linkProcessor,
    deviceType,
    deviceSerialNumber,
}: UseSsoLinkBatchParams): UseSsoLinkBatchResult => {
    const template = ssoTemplate || url;
    const formattedSsoTemplate = iccSsoService.getFormattedSsoTemplate({ssoTemplate: template, params});
    const ssoEntry = useSelector(makeSelectSsoLink(formattedSsoTemplate));
    const {status, expirationDate} = ssoEntry;
    const isUrl = Boolean(url);
    const resolvedLink = formattedSsoTemplate
        ? resolveSsoLinkUrlForOpenFromState(ssoEntry, Boolean(openInBluefy), isSsoCookiesForced)
        : undefined;
    const linkWithQuery = appendQueryStringTemplate(resolvedLink, queryStringTemplate);
    const isIos = userBrowserService.isIosDevice();

    useSsoLinkFetch({
        isUrl,
        template: formattedSsoTemplate,
        status,
        disabled,
        openInBluefy,
        isSsoCookiesForced,
    });
    useSsoLinkRefresh({expirationDate, formattedSsoTemplate, disabled});

    const openBluefyOrTab = (href: string) => {
        externalLinkService.openResolvedUrl({
            url: href,
            openInBluefy: Boolean(openInBluefy && isIos),
            ssoTemplate,
            externalLink: undefined,
            ssoParams: params,
            linkProcessor,
            deviceType,
            deviceSerialNumber,
            ssoLinkKey: formattedSsoTemplate ?? undefined,
            isSsoCookiesForced,
        });
    };

    let redirect: () => void | Promise<void>;

    if (linkWithQuery) {
        redirect = () => openBluefyOrTab(linkWithQuery);
    } else {
        redirect = async () => {
            dispatch!(showLoader(URL_LOADER_NAME));

            const includeSessionInfoForRedirect = shouldRequestSessionInfo({
                allowSessionInfo: isSsoCookieFlowEnabledForLink({openInBluefy, isSsoCookiesForced}),
            });
            const templateForRequest = formattedSsoTemplate ?? undefined;

            const links = await iccSsoService.getSsoLinks({
                ssoTemplates: [isUrl ? undefined : templateForRequest],
                urls: [isUrl ? templateForRequest : undefined],
                includeSessionInfo: includeSessionInfoForRedirect,
            });

            const hasTemplateForRequest = Boolean(templateForRequest);
            const hasLinks = Array.isArray(links) && links.length > 0;

            if (!hasTemplateForRequest || !hasLinks) {
                appErrorService.showGlobalError();
            } else {
                const entryAfterFetch = makeSelectSsoLink(templateForRequest)(getState!());
                const linkFromResponse = resolveSsoLinkUrlForOpenFromState(
                    entryAfterFetch,
                    Boolean(openInBluefy),
                    isSsoCookiesForced
                );

                if (linkFromResponse) {
                    const linkFromResponseWithQuery = appendQueryStringTemplate(linkFromResponse, queryStringTemplate);

                    openBluefyOrTab(linkFromResponseWithQuery);
                } else {
                    appErrorService.showGlobalError();
                }
            }

            dispatch!(hideLoader(URL_LOADER_NAME));
        };
    }

    return {redirect, link: linkWithQuery};
};

const useSsoLinkFetch = ({
    isUrl,
    template,
    status,
    disabled,
    openInBluefy,
    isSsoCookiesForced,
}: {
    isUrl: boolean;
    template: string | null;
    status?: unknown;
    disabled?: boolean;
    openInBluefy?: boolean;
    isSsoCookiesForced: boolean;
}) => {
    useEffect(() => {
        if (disabled) return;
        if (!template) return;
        if (status) return;
        if (isUrl && urls.includes(template)) return;
        if (!isUrl && ssoTemplates.includes(template)) return;

        batchAllowSsoCookies =
            batchAllowSsoCookies || isSsoCookieFlowEnabledForLink({openInBluefy, isSsoCookiesForced});

        if (isUrl) {
            urls.push(template);
        } else {
            ssoTemplates.push(template);
        }

        const ssoLinkDebounceTimeout = appConfig.getSsoLinkDebounceTimeout();
        const run = helpers.debounce(getSsoLinks, ssoLinkDebounceTimeout);

        run();
    }, [template, status, disabled, openInBluefy, isSsoCookiesForced]);
};

const getSsoLinks = () => {
    const includeSessionInfo = shouldRequestSessionInfo({
        allowSessionInfo: batchAllowSsoCookies,
    });

    iccSsoService.getSsoLinks({ssoTemplates, urls, includeSessionInfo});

    urls = [];
    ssoTemplates = [];
    batchAllowSsoCookies = false;
};

export default useSsoLinkBatch;
